<script setup lang="ts">
  import { onMounted, Ref, ref, watch, computed } from 'vue';
  // Components
  import { IconImage, DatePicker, SelectInput } from '@/shared/ui';
  // Assets
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import AddButton from '@/shared/assets/icons/AddButton.svg';
  import DeleteButton from '@/shared/assets/icons/DeleteButton.svg';
  // Types
  import type { ApiResponse, MembershipListItem, UserItem } from '@/shared/lib';
  // Utils
  import { formatCivilDateToPtBR, formatCollaboratorNameAndRegistration } from '@/shared/lib';
  import { useAuthStore } from '@/entities/Session';

  const authStore = useAuthStore();

  const props = defineProps<{
    users: ApiResponse<UserItem[]>;
    instructors?: UserItem[];
    resetTrainingSessions: boolean;
  }>();

  const INSTRUCTORS = computed(() => {
    const sourceUsers = props.instructors ?? props.users.data ?? [];
    return sourceUsers
      .filter((user): user is UserItem & { id: number } => user.id != null)
      .map((user) => ({
        id: user.id,
        name: formatCollaboratorNameAndRegistration(user.name ?? '', user.registrationNumber),
      }));
  });

  const trainingSections: Ref<
    {
      id: number;
      title: string;
      selectedInstructor: string | number;
      selectedInstructorPosition: string | number;
      selectedDate: Date | null;
      isDatePickerOpen: boolean;
    }[]
  > = ref([]);

  watch(
    () => props.resetTrainingSessions,
    (newValue) => {
      if (newValue) {
        trainingSections.value = [
          {
            id: 1,
            title: 'Data do treinamento:',
            selectedInstructor: '',
            selectedInstructorPosition: '',
            selectedDate: null,
            isDatePickerOpen: false,
          },
        ];
      }

      emit('update:resetTrainingSessions');
    }
  );

  const emit = defineEmits(['selectedTrainingSections', 'update:resetTrainingSessions']);

  watch(
    () => trainingSections.value.map((section) => section.selectedInstructor),
    (newInstructors, oldInstructors) => {
      if (oldInstructors) {
        newInstructors.forEach((newInstructor, index) => {
          const oldInstructor = oldInstructors[index];
          if (newInstructor !== oldInstructor) {
            trainingSections.value[index].selectedInstructorPosition = '';
            emit('selectedTrainingSections', trainingSections.value);
          }
        });
      }
    },
    { deep: true }
  );

  const addTrainingSection = () => {
    if (trainingSections.value.length < 5) {
      const newId = trainingSections.value.length + 1;
      trainingSections.value.push({
        id: newId,
        title: `${newId}ª Data do treinamento:`,
        selectedInstructor: '',
        selectedInstructorPosition: '',
        selectedDate: null,
        isDatePickerOpen: false,
      });

      emit('selectedTrainingSections', trainingSections.value);
    }
  };

  const removeTrainingSection = () => {
    const { id } = trainingSections.value[trainingSections.value.length - 1];
    trainingSections.value = trainingSections.value.filter((section) => section.id !== id);
    emit('selectedTrainingSections', trainingSections.value);
  };

  const toggleDatePicker = (sectionId: number) => {
    const section = trainingSections.value.find((s) => s.id === sectionId);
    if (section) {
      section.isDatePickerOpen = !section.isDatePickerOpen;
    }
  };

  const getInstructorPositions = (instructorId: string | number) => {
    if (!instructorId) return [];

    const instructor =
      props.instructors?.find((user) => user.id?.toString() === instructorId.toString()) ??
      props.users.data?.find((user) => user.id?.toString() === instructorId.toString());

    if (!instructor) return [];

    const currentBranchId = authStore.selectedBranchId;
    const memberships = currentBranchId
      ? instructor.memberships?.filter((m: MembershipListItem) => m.branchId === currentBranchId)
      : instructor.memberships;

    const userPositionsRaw =
      (memberships ?? []).flatMap((membership: any) => {
        let positions: { id: number; name: string }[] = [];

        if (membership.memberPositions) {
          positions = (membership.memberPositions ?? []).map(
            (mp: any) => mp.position
          );
        } else {
          positions =
            membership.positions ?? (membership.position ? [membership.position] : []);
        }

        return positions.map((p) => ({ id: p.id, name: p.name }));
      });

    const seenIds = new Set<number>();
    return userPositionsRaw.filter((position) => {
      if (seenIds.has(position.id)) {
        return false;
      }
      seenIds.add(position.id);
      return true;
    });
  };

  const formatDateDisplay = (date: Date | null): string => {
    if (!date) return 'Selecione uma data';
    return formatCivilDateToPtBR(date);
  };

  const isAddButtonDisabled = computed(() => trainingSections.value.length >= 5);

  const addButtonClasses = computed(() =>
    isAddButtonDisabled.value ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
  );

  onMounted(() => {
    trainingSections.value = [
      {
        id: 1,
        title: 'Data do treinamento:',
        selectedInstructor: '',
        selectedInstructorPosition: '',
        selectedDate: null,
        isDatePickerOpen: false,
      },
    ];

    emit('selectedTrainingSections', trainingSections.value);
  });
</script>

<template>
  <div
    v-for="section in trainingSections"
    :key="section.id"
    class="flex gap-2 px-6 py-8 items-center justify-between"
  >
    <div class="flex gap-6 items-center">
      <div v-if="INSTRUCTORS">
        <h3 class="text-left mb-2 font-semibold">
          Ministrante:
        </h3>
        <SelectInput
          v-model="section.selectedInstructor"
          :options="INSTRUCTORS"
          label="Selecione um colaborador"
          class="font-normal"
          @update:model-value="() => emit('selectedTrainingSections', trainingSections)"
        />
      </div>
      <div>
        <h3 class="text-left mb-2 font-semibold">
          Cargo:
        </h3>
        <SelectInput
          v-model="section.selectedInstructorPosition"
          :options="
            getInstructorPositions(section.selectedInstructor).length > 0
              ? getInstructorPositions(section.selectedInstructor)
              : [{ id: 0, name: 'Nenhum cargo disponível' }]
          "
          label="Selecione o cargo relacionado ao treinamento"
          class="font-normal"
          @update:model-value="() => emit('selectedTrainingSections', trainingSections)"
        />
      </div>
      <div>
        <h3 class="text-left mb-2 font-semibold">
          {{ section.title }}
        </h3>
        <button
          class="flex gap-2 bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-07 focus:outline-none focus:border-gray-07 font-medium"
          @click="toggleDatePicker(section.id)"
        >
          <IconImage
            :src="CalendarIcon"
            alt="Calendário"
          />
          {{ formatDateDisplay(section.selectedDate) }}
        </button>

        <DatePicker
          v-model:is-open="section.isDatePickerOpen"
          v-model:selected-date="section.selectedDate"
          :disable-past-dates="true"
          @update:selected-date="() => emit('selectedTrainingSections', trainingSections)"
          @apply="section.isDatePickerOpen = false"
          @cancel="section.isDatePickerOpen = false"
        />
      </div>
    </div>
    <div class="flex gap-2">
      <IconImage
        v-if="section.id > 1"
        :src="DeleteButton"
        alt="Remover"
        class="cursor-pointer text-[var(--theme-secondary-ink)] hover:opacity-80"
        @click="removeTrainingSection"
      />
      <IconImage
        v-if="section.id === 1"
        :src="AddButton"
        alt="Adicionar"
        :class="['cursor-pointer text-[var(--theme-secondary-ink)] hover:opacity-80', addButtonClasses]"
        @click="addTrainingSection"
      />
    </div>
  </div>
</template>
